import React, { useContext, useEffect, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CheckCircle from "@mui/icons-material/CheckCircle";
import WarningAmber from "@mui/icons-material/WarningAmber";
import { useQueryClient } from "@tanstack/react-query";
import { CachedTokenContext } from "@seabrookstudios/auth";
import { MyProfile, useMyProfile, useUpdateProfile } from "@seabrookstudios/pitch2table";
import { s2Url } from "@seabrookstudios/types";
import { useSnackbar } from "notistack";
import endpoints from "../endpoints";
import { useUploadImage } from "../users/queries";
import { valuesMatch } from "../nestifyz/compareProfile";
import CardHeader from "@mui/material/CardHeader";
import SyncAlt from "@mui/icons-material/SyncAlt";
import CardActions from "@mui/material/CardActions";

/**
 * @param {object} props
 * @param {boolean} props.matches
 */
const ComparisonStatus = ({ matches }) => {
  if (matches) {
    return <Chip icon={<CheckCircle />} color="success" label="SAME" />;
  }

  return <Chip icon={<WarningAmber />} color="warning" label="DIFFERENT" />;
};

/**
 * @param {object} props
 * @param {string|undefined} props.src
 * @param {string} props.label
 */
const AvatarPreview = ({ src, label }) => {
  return (
    <Stack alignItems="center" spacing={1}>
      <Avatar src={src} variant="rounded" sx={{ width: 80, height: 80 }} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
};

/**
 * @param {object} props
 * @param {File} props.avatarFile
 */
const ImportedAvatarPreview = ({ avatarFile }) => {
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    const objectUrl = URL.createObjectURL(avatarFile);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [avatarFile]);

  return <AvatarPreview src={previewUrl} label="NestiFyz avatar" />;
};

/**
 * @param {object} props
 * @param {string} props.importedName
 */
const UseImportedPreferredNameButton = ({ importedName }) => {
  const updateProfile = useUpdateProfile();

  const onUpdate = async () => {
    await updateProfile.mutateAsync({
      toUpdate: {
        preferredName: importedName,
        firstName: undefined,
        lastName: undefined,
        pronouns: undefined,
        contactNumber: undefined,
        city: undefined,
        country: undefined,
        timezone: undefined,
        marketingConsent: undefined,
        sendDailyEmails: undefined,
        sendNotifications: undefined,
        whenToSendDailyEmails: undefined,
        preferredCurrency: undefined,
        slug: undefined,
        isPublic: undefined,
        showEmail: undefined,
        travel: undefined,
        isTravelling: undefined,
      },
    });
  };

  return (
    <Button
      disabled={updateProfile.isPending}
      onClick={onUpdate}
      startIcon={<SyncAlt />}
      variant="contained"
      color="primary"
    >
      UPDATE
    </Button>
  );
};

/**
 * @param {object} props
 * @param {string} props.location
 */
const UseImportedLocationButton = ({ location }) => {
  const updateProfile = useUpdateProfile();

  const onUpdate = async () => {
    await updateProfile.mutateAsync({
      toUpdate: {
        preferredName: undefined,
        firstName: undefined,
        lastName: undefined,
        pronouns: undefined,
        contactNumber: undefined,
        city: location,
        country: undefined,
        timezone: undefined,
        marketingConsent: undefined,
        sendDailyEmails: undefined,
        sendNotifications: undefined,
        whenToSendDailyEmails: undefined,
        preferredCurrency: undefined,
        slug: undefined,
        isPublic: undefined,
        showEmail: undefined,
        travel: undefined,
        isTravelling: undefined,
      },
    });
  };

  return (
    <Button
      disabled={updateProfile.isPending}
      onClick={onUpdate}
      startIcon={<SyncAlt />}
      variant="contained"
      color="primary"
    >
      UPDATE
    </Button>
  );
};

/**
 * @param {object} props
 * @param {File} props.avatarFile
 */
const UseImportedAvatarButton = ({ avatarFile }) => {
  const token = useContext(CachedTokenContext);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);

  const onSuccess = async () => {
    setIsUploading(false);
    enqueueSnackbar("Avatar updated.", { variant: "success" });
    await queryClient.invalidateQueries({ queryKey: MyProfile });
  };

  const onError = () => {
    setIsUploading(false);
    enqueueSnackbar("Avatar update failed. Please try again.", { variant: "error" });
  };

  const { users } = endpoints();
  const url = users().avatar();
  const uploadImage = useUploadImage(s2Url(url), token, "avatar", onSuccess, onError);

  const onUpload = () => {
    setIsUploading(true);
    uploadImage(
      "avatar",
      avatarFile,
      {},
      () => {},
      () => {
        setIsUploading(false);
      },
      () => {},
      () => {}
    );
  };

  return (
    <Button disabled={isUploading} onClick={onUpload} startIcon={<SyncAlt />} variant="contained" color="primary">
      UPDATE
    </Button>
  );
};

/**
 * @param {object} props
 * @param {string} props.label
 * @param {string|undefined} props.value
 */
const AccountField = ({ label, value }) => {
  return (
    <ListItem>
      <ListItemText primary={label} secondary={value || "Not set"} />
    </ListItem>
  );
};

/**
 * @param {object} props
 * @param {import("../nestifyz/parseExport").NestiFyzDesignerProfile} props.importedProfile
 */
export const NestiFyzProfileComparison = ({ importedProfile }) => {
  const { data: profile, isLoading } = useMyProfile();

  if (isLoading) {
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!profile) {
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography color="text.secondary">Unable to load your profile for comparison.</Typography>
        </CardContent>
      </Card>
    );
  }

  const importedAvatarFile = importedProfile.avatarFile;
  const preferredNameMatches = valuesMatch(profile.preferredName, importedProfile.name);
  const locationMatches = valuesMatch(profile.location.city, importedProfile.location);
  const hasImportedProfileData = Boolean(importedProfile.name || importedProfile.location || importedAvatarFile);

  if (!hasImportedProfileData) {
    return null;
  }

  return (
    <>
      {importedAvatarFile && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title="Avatar" />
          <CardContent>
            <List disablePadding dense>
              <ListItem>
                <Stack direction="row" spacing={1}>
                  <AvatarPreview
                    src={profile.pictureUrl}
                    label={profile.pictureUrl ? "Current avatar" : "No avatar set"}
                  />
                  <ImportedAvatarPreview avatarFile={importedAvatarFile} />
                </Stack>
              </ListItem>
            </List>
          </CardContent>
          <CardActions>
            <UseImportedAvatarButton avatarFile={importedAvatarFile} />
          </CardActions>
        </Card>
      )}
      {importedProfile.name && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title="Preferred Name" action={<ComparisonStatus matches={Boolean(preferredNameMatches)} />} />
          {!preferredNameMatches && (
            <CardContent>
              <List disablePadding dense>
                <AccountField label="Current" value={profile.preferredName} />
                <AccountField label="NestiFyz" value={importedProfile.name} />
              </List>
            </CardContent>
          )}
          {!preferredNameMatches && (
            <CardActions>
              <UseImportedPreferredNameButton importedName={importedProfile.name} />
            </CardActions>
          )}
        </Card>
      )}
      {importedProfile.location && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title="City" action={<ComparisonStatus matches={!!locationMatches} />} />
          {!locationMatches && (
            <CardContent>
              <List disablePadding dense>
                <AccountField label="Current" value={profile.location.city} />
                <AccountField label="NestiFyz" value={importedProfile.location} />
              </List>
            </CardContent>
          )}
          {!locationMatches && (
            <CardActions>
              <UseImportedLocationButton location={importedProfile.location} />
            </CardActions>
          )}
        </Card>
      )}
    </>
  );
};
